import { IContentMutationPayload } from '@/editor/editor-block/store/types/mutations';
import {
  INodePayload,
  IRowPayload,
  ICellPayload,
  INodeUpdatePayload,
} from '@/editor/types/models-update';
import {
  ICell,
  IEditorBlock,
  INode,
  IRow,
  ISection,
  ITableStyle,
  CustomMergeTagPayload,
  ITable,
  IText,
  IImage,
} from '@/editor/types';
import {
  EDividerPadding,
  EDividerType,
  EImageAlign,
  EImageSize,
  ENodeType,
} from '@/editor/types/enums';
import { fallbackTableContent, fallbackTextContent } from '@/editor/editor-block/constants';

export class ContentMutationHelper {
  static updateNode(cell: ICell, { update, targetNodeId }: IContentMutationPayload): ICell {
    const mergeNodeValues = (node: INode, update: INodeUpdatePayload): INode => {
      return {
        ...node,
        ...update,
        tableStyle: {
          ...(node as ITable).tableStyle,
          ...update.tableStyle,
        },
        imageTransforms:
          update.imageTransforms === null
            ? update.imageTransforms
            : {
                ...(node as IImage).imageTransforms,
                ...update.imageTransforms,
              },
      };
    };

    return {
      ...cell,
      nodes: cell.nodes.map((node: INode) =>
        node.id === targetNodeId ? mergeNodeValues(node, update) : node
      ),
    };
  }

  static getNewRow({ rowId: id, rowType: type, cells }: IRowPayload): IRow {
    return {
      id,
      type,
      cells: cells.map(ContentMutationHelper.getNewCell),
    };
  }

  static getNewCell({ width, cellId: id, cellType: type, nodes }: ICellPayload) {
    return {
      width,
      id,
      type,
      nodes: nodes.map(ContentMutationHelper.getNewNode),
    };
  }

  private static getNewTextNode({
    content,
    fields,
    nodeType: type,
    nodeId: id,
  }: INodePayload): IText {
    return {
      id,
      type,
      content: content || fallbackTextContent,
      fields: fields || [],
    };
  }

  private static getNewImageNode({
    nodeType: type,
    nodeId: id,
    imageId,
    srcUrl,
    linkUrl,
    imageSize,
    caption,
    heading,
    imageAlign,
    imageTransforms,
  }: INodePayload): IImage {
    return {
      id,
      type,
      imageId: imageId || '',
      srcUrl: srcUrl || '',
      linkUrl: linkUrl || '',
      imageSize: imageSize || EImageSize.Original,
      caption: caption || '',
      heading: heading || '',
      imageAlign: imageAlign || EImageAlign.Center,
      imageTransforms: imageTransforms || null,
    };
  }

  private static getNewTableNode({
    nodeType: type,
    nodeId: id,
    content,
    tableStyle,
    fields,
  }: INodePayload): ITable {
    return {
      id,
      type,
      content: content || fallbackTableContent,
      tableStyle: tableStyle || ({} as ITableStyle),
      fields: fields || [],
    };
  }

  private static getNewDividerNode({
    nodeType: type,
    nodeId: id,
    dividerType,
    dividerPadding,
  }: INodePayload): INode {
    return {
      id,
      type,
      dividerType: dividerType || EDividerType.Thin,
      dividerPadding: dividerPadding || EDividerPadding.None,
    };
  }

  static getNewNode(payload: INodePayload): INode {
    switch (payload.nodeType) {
      case ENodeType.Text:
        return ContentMutationHelper.getNewTextNode(payload);

      case ENodeType.Image:
        return ContentMutationHelper.getNewImageNode(payload);

      case ENodeType.Table:
        return ContentMutationHelper.getNewTableNode(payload);

      case ENodeType.Divider:
        return ContentMutationHelper.getNewDividerNode(payload);

      default:
        return undefined;
    }
  }

  static mapBlock(block: IEditorBlock, payload: IContentMutationPayload): IEditorBlock {
    if (block.contentId === payload.contentId) {
      return {
        ...block,
        content: {
          ...block.content,
          sections: block.content.sections.map((section: ISection) =>
            section.id === payload.targetSectionId
              ? payload.updateSection(section, payload)
              : section
          ),
        },
      };
    }
    return block;
  }

  static addRow(
    section: ISection,
    { targetRowId, row, appendRow }: IContentMutationPayload
  ): ISection {
    const index: number = section.rows.findIndex(({ id }: IRow) => id === targetRowId);

    if (index === -1) {
      return {
        ...section,
        rows: [...section.rows, row],
      };
    }

    return {
      ...section,
      ...section.rows.splice(index + Number(!!appendRow), 0, row),
    };
  }

  static deleteCell(row: IRow, { targetCellId, targetRowId }: IContentMutationPayload): IRow {
    return row.id === targetRowId
      ? {
          ...row,
          cells: row.cells.filter(({ id }: ICell) => id !== targetCellId),
        }
      : row;
  }

  static deleteRow(section: ISection, { targetRowId }: IContentMutationPayload): ISection {
    return {
      ...section,
      rows: section.rows.filter(({ id }: IRow) => id !== targetRowId),
    };
  }

  static mapSection(section: ISection, payload: IContentMutationPayload): ISection {
    return {
      ...section,
      rows: section.rows.map((row: IRow) => payload.updateRow(row, payload)),
    };
  }

  static mapRow(row: IRow, payload: IContentMutationPayload): IRow {
    if (row.id === payload.targetRowId) {
      return {
        ...row,
        cells: row.cells.map((cell: ICell) => payload.updateCell(cell, payload)),
      };
    }
    return row;
  }

  static addCell(
    row: IRow,
    { targetRowId, targetCellId, cell, appendCell }: IContentMutationPayload
  ): IRow {
    if (row.id !== targetRowId) {
      return row;
    }

    const index: number = row.cells.findIndex(({ id }: ICell) => id === targetCellId);

    if (index === -1) {
      return {
        ...row,
        cells: [...row.cells, cell],
      };
    }

    return {
      ...row,
      ...row.cells.splice(index + Number(!!appendCell), 0, cell),
    };
  }

  static mapCell(cell: ICell, payload: IContentMutationPayload): ICell {
    return cell.id === payload.targetCellId ? payload.updateNode(cell, payload) : cell;
  }

  static addNodes(
    cell: ICell,
    { targetNodeId, nodes, appendNode }: IContentMutationPayload
  ): ICell {
    const index: number = cell.nodes.findIndex(({ id }: INode) => id === targetNodeId);

    if (index === -1) {
      return {
        ...cell,
        nodes: [...cell.nodes, ...nodes],
      };
    }

    return {
      ...cell,
      ...cell.nodes.splice(index + Number(!!appendNode), 0, ...nodes),
    };
  }

  static deleteNodes(cell: ICell, { targetNodeIds }: IContentMutationPayload): ICell {
    return {
      ...cell,
      nodes: cell.nodes.filter(({ id }: INode) => !targetNodeIds.includes(id)),
    };
  }

  static cascadeUpdateCustomMergeTags(
    blocks: IEditorBlock[],
    payload: CustomMergeTagPayload
  ): IEditorBlock[] {
    const updateFields = (
      node: ITable | IText,
      { customName, value }: CustomMergeTagPayload
    ): INode => {
      return {
        ...node,
        fields: node.fields.map(field =>
          field.customName === customName ? { ...field, value } : field
        ),
      };
    };

    return blocks.map((block: IEditorBlock) => ({
      ...block,
      content: {
        ...block.content,
        sections: block.content.sections.map((section: ISection) => ({
          ...section,
          rows: section.rows.map((row: IRow) => ({
            ...row,
            cells: row.cells.map((cell: ICell) => ({
              ...cell,
              nodes: cell.nodes.map((node: IText | ITable) =>
                node.fields ? updateFields(node, payload) : node
              ),
            })),
          })),
        })),
      },
    }));
  }
}
